import React from "react";
import { type Project, projectService } from "../services/projects";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "nova:lastProjectId";

interface ProjectContextValue {
	projects: Project[];
	selectedProject: Project | null;
	selectedProjectId: string | null;
	isLoading: boolean;
	error: Error | null;
	selectProject: (projectId: string) => void;
	refreshProjects: () => Promise<void>;
}

const ProjectContext = React.createContext<ProjectContextValue | undefined>(
	undefined,
);

export function ProjectProvider({ children }: { children: React.ReactNode }) {
	const { isAuthenticated, isLoading: authLoading, user } = useAuth();
	const [projects, setProjects] = React.useState<Project[]>([]);
	const [selectedProjectId, setSelectedProjectId] = React.useState<
		string | null
	>(null);
	const [isLoading, setIsLoading] = React.useState(false);
	const [error, setError] = React.useState<Error | null>(null);

	const isSuperAdmin = user?.role === "superAdmin";

	const applySelection = React.useCallback(
		(list: Project[], savedId: string | null) => {
			// Super admins don't require project selection
			if (isSuperAdmin) {
				// Allow super admins to have no project selected
				if (savedId && list.some((p) => p.id === savedId)) {
					setSelectedProjectId(savedId);
					localStorage.setItem(STORAGE_KEY, savedId);
				} else {
					// Don't auto-select for super admins
					setSelectedProjectId(null);
				}
				return;
			}

			// Regular users require project selection
			const fallback = list[0]?.id ?? null;
			if (savedId && list.some((p) => p.id === savedId)) {
				setSelectedProjectId(savedId);
				localStorage.setItem(STORAGE_KEY, savedId);
				return;
			}
			// Clear invalid project ID from localStorage
			if (savedId && !fallback) {
				localStorage.removeItem(STORAGE_KEY);
			}
			setSelectedProjectId(fallback);
			if (fallback) localStorage.setItem(STORAGE_KEY, fallback);
		},
		[isSuperAdmin],
	);

	const refreshProjects = React.useCallback(async () => {
		// Don't fetch if not authenticated
		if (!isAuthenticated) {
			return;
		}
		// Super admins can work without projects, but we still fetch them for reference
		setIsLoading(true);
		setError(null);
		try {
			const list = await projectService.list();
			setProjects(list);
			const saved = localStorage.getItem(STORAGE_KEY);
			applySelection(list, saved);
		} catch (err) {
			console.error("Failed to load projects", err);
			setError(
				err instanceof Error ? err : new Error("Failed to load projects"),
			);
		} finally {
			setIsLoading(false);
		}
	}, [applySelection, isAuthenticated]);

	// Load projects and restore last selection - only after auth is complete
	React.useEffect(() => {
		if (!authLoading && isAuthenticated) {
			void refreshProjects();
		} else if (!authLoading && !isAuthenticated) {
			// Clear projects when user logs out
			setProjects([]);
			setSelectedProjectId(null);
			setError(null);
		}
	}, [refreshProjects, authLoading, isAuthenticated]);

	const selectProject = React.useCallback((projectId: string) => {
		setSelectedProjectId(projectId);
		localStorage.setItem(STORAGE_KEY, projectId);
	}, []);

	const selectedProject = React.useMemo(
		() => projects.find((p) => p.id === selectedProjectId) ?? null,
		[projects, selectedProjectId],
	);

	const value: ProjectContextValue = {
		projects,
		selectedProject,
		selectedProjectId,
		isLoading,
		error,
		selectProject,
		refreshProjects,
	};

	return (
		<ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
	);
}

export function useProjectContext() {
	const ctx = React.useContext(ProjectContext);
	if (!ctx) {
		throw new Error("useProjectContext must be used within a ProjectProvider");
	}
	return ctx;
}

// Backwards-compatible alias
export const useProject = useProjectContext;
